import { useQuery } from 'react-query'
import { challengesAPI, submissionsAPI, usersAPI } from '../../services/api'
import {
  ChartBarIcon,
  TrophyIcon,
  DocumentTextIcon,
  UserGroupIcon,
  CalendarIcon,
  StarIcon
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../ui/LoadingSpinner'
import { format, subDays, startOfDay, endOfDay } from 'date-fns'

export default function AdminAnalytics() {
  // Fetch analytics data
  const { data: analyticsData, isLoading } = useQuery(
    'admin-analytics',
    async () => {
      try {
        const [challengesRes, submissionsRes, usersRes] = await Promise.allSettled([
          challengesAPI.getAll(),
          submissionsAPI.getAll({ limit: 1000 }),
          usersAPI.getAll({ limit: 1000 })
        ])
        
        const challenges = challengesRes.status === 'fulfilled' ? challengesRes.value.data.challenges : []
        const submissions = submissionsRes.status === 'fulfilled' ? submissionsRes.value.data.submissions : []
        const users = usersRes.status === 'fulfilled' ? (usersRes.value.data.users || []) : []
        
        console.log('Admin analytics data:', { challenges: challenges.length, submissions: submissions.length, users: users.length })

        // Calculate metrics
        const now = new Date()
        const last30Days = subDays(now, 30)
        const last7Days = subDays(now, 7)

        const newUsersLast30Days = users.filter(u => 
          u.created_at && new Date(u.created_at) >= last30Days
        ).length

        const newUsersLast7Days = users.filter(u => 
          u.created_at && new Date(u.created_at) >= last7Days
        ).length

        const submissionsLast30Days = submissions.filter(s => 
          s.created_at && new Date(s.created_at) >= last30Days
        ).length

        const submissionsLast7Days = submissions.filter(s => 
          s.created_at && new Date(s.created_at) >= last7Days
        ).length

        const averageScore = submissions.length > 0 ? 
          submissions
            .filter(s => s.score !== null && s.score !== undefined)
            .reduce((acc, s, _, arr) => arr.length > 0 ? acc + s.score / arr.length : 0, 0) : 0

        const categoryStats = challenges.reduce((acc, challenge) => {
          if (challenge.category) {
            acc[challenge.category] = (acc[challenge.category] || 0) + 1
          }
          return acc
        }, {})

        const statusStats = challenges.reduce((acc, challenge) => {
          if (challenge.status) {
            acc[challenge.status] = (acc[challenge.status] || 0) + 1
          }
          return acc
        }, {})

        return {
          totalChallenges: challenges.length,
          totalSubmissions: submissions.length,
          totalUsers: users.length,
          activeChallenges: challenges.filter(c => c.status === 'active').length,
          pendingSubmissions: submissions.filter(s => s.status === 'submitted').length,
          evaluatedSubmissions: submissions.filter(s => s.status === 'evaluated').length,
          newUsersLast30Days,
          newUsersLast7Days,
          submissionsLast30Days,
          submissionsLast7Days,
          averageScore: averageScore || 0,
          categoryStats,
          statusStats
        }
      } catch (error) {
        console.error('Failed to fetch admin analytics:', error)
        // Return empty data structure if all API calls fail
        return {
          totalChallenges: 0,
          totalSubmissions: 0,
          totalUsers: 0,
          activeChallenges: 0,
          pendingSubmissions: 0,
          evaluatedSubmissions: 0,
          newUsersLast30Days: 0,
          newUsersLast7Days: 0,
          submissionsLast30Days: 0,
          submissionsLast7Days: 0,
          averageScore: 0,
          categoryStats: {},
          statusStats: {}
        }
      }
    },
    {
      retry: false,
      onError: (error) => {
        console.error('Admin analytics query failed:', error)
      }
    }
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const stats = analyticsData || {}

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrophyIcon className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Challenges
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.totalChallenges || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Submissions
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.totalSubmissions || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserGroupIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Users
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.totalUsers || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <StarIcon className="h-6 w-6 text-purple-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Avg Score
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.averageScore ? stats.averageScore.toFixed(1) : 'N/A'}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Growth Metrics */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarIcon className="h-6 w-6 text-indigo-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    New Users (30d)
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.newUsersLast30Days || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    New Users (7d)
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.newUsersLast7Days || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-6 w-6 text-orange-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Submissions (30d)
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.submissionsLast30Days || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Submissions (7d)
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.submissionsLast7Days || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category and Status Breakdown */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Challenge Categories */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Challenges by Category
            </h3>
            {Object.keys(stats.categoryStats || {}).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats.categoryStats).map(([category, count]) => (
                  <div key={category} className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">{category}</span>
                    <span className="text-sm text-gray-500">{count} challenges</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No categories available</p>
            )}
          </div>
        </div>

        {/* Challenge Status */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Challenges by Status
            </h3>
            {Object.keys(stats.statusStats || {}).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats.statusStats).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900 capitalize">
                      {status.replace('_', ' ')}
                    </span>
                    <span className="text-sm text-gray-500">{count} challenges</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No status data available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}